import { InteractionStatus } from "@azure/msal-browser";
import { useMsal, useIsAuthenticated } from "@azure/msal-react";

export const IsAuthenticated = () => {
  const isAuthenticated = useIsAuthenticated();
  const { inProgress } = useMsal();
  return inProgress === InteractionStatus.None && isAuthenticated;
};

export const getToken = () => {
  return localStorage.getItem("token");
};

export const isTokenValid = (token) => {
  if (!token) return false;

  try {
    const payload = JSON.parse(atob(token.split(".")[1]));
    const expiration = payload.exp;
    const currentTime = Math.floor(Date.now() / 1000); // Current time in seconds

    return expiration > currentTime;
  } catch (e) {
    return false;
  }
};

export const parseJwt = (token) => {
  try {
    return JSON.parse(atob(token.split(".")[1]));
  } catch {
    return {};
  }
};

export const getRoles = () => {
  const t = getToken();
  if (!t) return [];
  const p = parseJwt(t);
  return Array.isArray(p.roles) ? p.roles : [];
};

// Role names
export const ROLES = {
  SCTK: "ROLE_USER_GLOBAL_SCTK_ALL",
  RELEASE_MANAGEMENT: "ROLE_USER_GLOBAL_RELEASE_MANAGEMENT_ALL",
  MULTI_DEPLOYMENT: "ROLE_USER_GLOBAL_MULTI_DEPLOYMENT_ALL",
  AWS_SECRETS_MANAGER: "ROLE_USER_ITAAP_AWS_SECRETS_MANAGER_ALL",
  AWS_PARAMETER_STORE_MANAGER: "ROLE_USER_ITAAP_AWS_PARAMETER_STORE_MANAGER_ALL",
  AWS_CACHE_MANAGER: "ROLE_USER_ITAAP_AWS_CACHE_MANAGER_ALL",
  IAC_TFSTATE_UNLOCK: "ROLE_USER_GLOBAL_IAC_TFSTATE_UNLOCK_ALL",
  UTILS: "ROLE_USER_GLOBAL_UTILS_ALL",
  ROLE_MANAGEMENT: "ROLE_USER_ADMIN_ROLE_MANAGEMENT_ALL",
  USER_MANAGEMENT: "ROLE_USER_ADMIN_USER_MANAGEMENT_ALL",
  TENANT_MANAGEMENT: "ROLE_USER_ADMIN_TENANT_MANAGEMENT_ALL",
  AI_MODE: "ROLE_USER_GLOBAL_AI_MODE_ALL"
};

export const hasRole = (role) => getRoles().includes(role);

export const ROLE_BASED_ACCESS = {
  SCTK: () => hasRole(ROLES.SCTK),
  RELEASE_MANAGEMENT: () => hasRole(ROLES.RELEASE_MANAGEMENT),
  MULTI_DEPLOYMENT: () => hasRole(ROLES.MULTI_DEPLOYMENT),
  AWS_MANAGEMENT: () =>
    hasRole(ROLES.AWS_SECRETS_MANAGER) ||
    hasRole(ROLES.AWS_PARAMETER_STORE_MANAGER) ||
    hasRole(ROLES.AWS_CACHE_MANAGER),
  AWS_SECRETS_MANAGER: () => hasRole(ROLES.AWS_SECRETS_MANAGER),
  AWS_PARAMETER_STORE_MANAGER: () => hasRole(ROLES.AWS_PARAMETER_STORE_MANAGER),
  AWS_CACHE_MANAGER: () => hasRole(ROLES.AWS_CACHE_MANAGER),
  IAC_TFSTATE_UNLOCK: () => hasRole(ROLES.IAC_TFSTATE_UNLOCK),
  UTILS: () => hasRole(ROLES.UTILS),
  ROLE_MANAGEMENT: () => hasRole(ROLES.ROLE_MANAGEMENT),
  USER_MANAGEMENT: () => hasRole(ROLES.USER_MANAGEMENT),
  TENANT_MANAGEMENT: () => hasRole(ROLES.TENANT_MANAGEMENT),
  AI_MODE: () => hasRole(ROLES.AI_MODE),
};

// Map routes to required roles
const PATH_ROLE = {
  "/sctk": ROLES.SCTK,
  "/multi-deployment": ROLES.MULTI_DEPLOYMENT,
  "/gitflow-release-management": ROLES.RELEASE_MANAGEMENT,
  "/gitflow-hotfix-management": ROLES.RELEASE_MANAGEMENT,
  "/tbd-release-management": ROLES.RELEASE_MANAGEMENT,
  "/tbd-hotfix-management": ROLES.RELEASE_MANAGEMENT,
  "/aws-secrets-manager": ROLES.AWS_SECRETS_MANAGER,
  "/aws-parameter-store-manager": ROLES.AWS_PARAMETER_STORE_MANAGER,
  "/aws-cache-manager": ROLES.AWS_CACHE_MANAGER,
  "/iac-tfstate-unlock": ROLES.IAC_TFSTATE_UNLOCK,
  "/util": ROLES.UTILS,
  "/util/git": ROLES.UTILS,
  "/util/crypto": ROLES.UTILS,
  "/util/keys": ROLES.UTILS,
  "/util/git/pr-management": ROLES.UTILS,
  "/role-management": ROLES.ROLE_MANAGEMENT,
  "/user-management": ROLES.USER_MANAGEMENT,
  "/tenant-management": ROLES.TENANT_MANAGEMENT,
  "/ai-mode": ROLES.AI_MODE,
};

export const isPathAllowed = (path) => {
  const normalized = path.toLowerCase();
  const need = PATH_ROLE[normalized];
  if (!need) return true;
  return hasRole(need);
};

export const getTokenIssuer = () => {
  const t = getToken();
  if (!t) return [];
  const p = parseJwt(t);
  return p.iss ? p.iss : "";
};

export const getTenants = () => {
  const t = getToken();
  if (!t) return [];
  const p = parseJwt(t);
  return Array.isArray(p.tenants) ? p.tenants : [];
};

export const getUserType = () => {
  const t = getToken();
  if (!t) return "";
  const p = parseJwt(t);
  return p.userType || p.type || "";
};
